import Popover from "components/Popover/Popover";
import ReceivableInvoice from "./ReceivableInvoice";
import { useEffect, useRef, useState } from "react";
import { commonService } from "Dashboard/services/common.service";
import { IOption } from "Dashboard/domain/IOption";
import { partnersService } from "Dashboard/scenes/Partners/services/partner.service";
import { IInvoiceReceivable } from "Dashboard/scenes/Partners/domain/IInvoiceReceivable";
import { IInvoiceReceivableParams } from "Dashboard/scenes/Partners/domain/IInvoiceReceivableParams";
import ReceivableInvoicePrint from "./ReceivableInvoicePrint";
import { selectState, setInvoicePopover } from "../accountReceivable.slice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks/hooks";
import { IStringOption } from "Dashboard/domain/IStringOption";
import { IUnit } from "Dashboard/domain/IUnit";
import { usePagePrint } from "hooks/usePagePrint";

const PopoverInvoice = () => {
  const dispatch = useAppDispatch();
  const {
    invoicePopover,
    indexForInvoice
  } = useSelector(selectState);
  const [units, setUnits] = useState<IUnit[]>([]);
  const [unitOptions, setUnitOptions] = useState<IStringOption[]>([]);
  const [municipalityOptions, setMunicipalityOptions]
    = useState<IOption[]>([]);

  const [invoiceInfo, setInvoiceInfo] = useState<IInvoiceReceivable>();

  const handleDataChange = (
    key: string,
    value: string | Date | number | null
  ) => {
    if (invoiceInfo) {
      const updatedInvoiceInfo = {
        ...invoiceInfo,
        [key]: value,
      };
      handleUpdateInvoice(updatedInvoiceInfo);
    }
  };

  const handleItemChange = (index: number, key: string, value: string) => {
    console.log("item change", index, key, value);
    if (invoiceInfo) {
      const updatedItems = [...(invoiceInfo.items || [])];
      let valueToUpdate;
      if (key === 'unit') {
        valueToUpdate = units.filter((u) => u.unitId === value)[0];
      } else {
        valueToUpdate = value;
      }
      updatedItems[index] = {
        ...updatedItems[index],
        [key]: valueToUpdate,
      };

      const updatedInvoiceInfo = {
        ...invoiceInfo,
        items: updatedItems,
      };
      console.log("item change", updatedInvoiceInfo);

      handleUpdateInvoice(updatedInvoiceInfo);
    }
  };

  const [deleteLoaderVisible, setDeleteLoaderVisible] = useState(false);

  const handleItemDelete = (indexToDelete: number) => {
    if (invoiceInfo?.items && invoiceInfo?.items.length > 1) {
      setDeleteLoaderVisible(true);
      const updatedItems = [...(invoiceInfo.items || [])];
      updatedItems.splice(indexToDelete, 1);

      const updatedInvoiceInfo = {
        ...invoiceInfo,
        items: updatedItems,
      };

      handleUpdateInvoice(updatedInvoiceInfo);
    }
  };

  const handleUpdateInvoice = async (
    updatedInvoiceInfo: IInvoiceReceivable
  ) => {
    if (updatedInvoiceInfo) {
      const updatedInvoiceParams: IInvoiceReceivableParams = {
        invoiceDate: updatedInvoiceInfo.invoiceDate,
        dueDate: updatedInvoiceInfo.dueDate,
        supplyDateStart: updatedInvoiceInfo.supplyDateStart,
        supplyDateEnd: updatedInvoiceInfo.supplyDateEnd,
        municipalityId:
          updatedInvoiceInfo.municipalityId ||
          updatedInvoiceInfo.municipality?.municipalityId ||
          undefined,
        items: updatedInvoiceInfo.items.map((item) => ({
          title: item.title || "",
          quantity: item.quantity || 0,
          unit: item.unit?.unitId || "KOM",
          unitPrice: item.unitPrice || 0,
          rebatePercentage: item.rebatePercentage || 0,
          vatPercentage: item.vatPercentage || 20,
        })),
      };

      try {
        if (invoiceInfo?.receivableId) {
          const updatedInvoice = await partnersService.updateReceivableInvoice(
            invoiceInfo?.receivableId,
            updatedInvoiceParams
          );
          setInvoiceInfo(updatedInvoice);
          setDeleteLoaderVisible(false);
        }
      } catch (err) {
        console.error("Error fetching invoice info:", err);
      }
    }
    return null;
  };

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = usePagePrint(componentRef, { margins: "0" });

  useEffect(() => {
    async function getUnitData() {
      const units = await commonService.getUnits({});
      const op = units.map((unit) => {
        return {
          label: unit.name,
          value: unit.unitId,
        };
      });
      setUnits(units);
      setUnitOptions(op);
    }
    async function getMunicipalityData() {
      const municipalities = await partnersService.getAllMunicipalities();
      const op = municipalities.map((municipality) => {
        return {
          label: municipality.name,
          value: municipality.municipalityId,
        };
      });
      setMunicipalityOptions(op);
    }

    try {
      getUnitData();
      getMunicipalityData();
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    setDeleteLoaderVisible(true);

    async function getInvoiceData() {
      try {
        const invoiceInfo = await partnersService.getReceivableInvoice(
          indexForInvoice
        );
        setInvoiceInfo(invoiceInfo);
      } catch (err) {
        console.log("Error getting invoice data: ", err);
      } finally {
        setDeleteLoaderVisible(false);
      }
    }
    if (indexForInvoice !== 0) {
      getInvoiceData();
    }
  }, [indexForInvoice]);

  return (
    <Popover
      isOpen={invoicePopover.isOpen}
      onClose={() =>
        dispatch(setInvoicePopover({ isOpen: false, top: 0, left: 0 }))
      }
      title="Info"
      direction="right"
      arrowStyle={{ top: "51%" }}
      position={{
        top: invoicePopover.top,
        left: invoicePopover.left,
        offsetLeft: invoicePopover.offsetLeft,
        offsetTop: invoicePopover.offsetTop,
      }}
    >
      <ReceivableInvoice
        invoiceInfo={invoiceInfo}
        handleDataChange={handleDataChange}
        handleItemChange={handleItemChange}
        handleItemDelete={handleItemDelete}
        unitOptions={unitOptions}
        municipalityOptions={municipalityOptions}
        deleteLoaderVisible={deleteLoaderVisible}
        handlePrint={handlePrint}
      />

      <div ref={componentRef} className="print-area">
        <ReceivableInvoicePrint
          id={invoiceInfo?.receivableId}
          invoiceInfo={invoiceInfo}
        />
      </div>
    </Popover>
  );
};

export default PopoverInvoice;
